import { env } from "@/env";
import { authenticatedFetch } from "@/lib/auth/authenticatedFetch";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { z } from "zod";

// `claiminiId` and the `entityType` discriminant go after the spread so a `.loose()`
// passthrough key from the payload can't overwrite them.
const incidentHitSchema = z
  .object({ claId: z.string() })
  .loose()
  .transform(({ claId, ...rest }) => ({ ...rest, claiminiId: claId, entityType: "incident" as const }));

const personHitSchema = z
  .object({
    claId: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    company: z.string().optional(),
    // We only need the city for now
    addresses: z.array(z.object({ city: z.string().optional() }).loose()).optional(),
  })
  .loose()
  .transform(({ claId, ...rest }) => ({ ...rest, claiminiId: claId, entityType: "person" as const }));

export type IncidentHit = z.infer<typeof incidentHitSchema>;
export type PersonHit = z.infer<typeof personHitSchema>;
/** Discriminated on `entityType`. */
export type EntityHit = IncidentHit | PersonHit;

const incidentSearchResponseSchema = z.array(incidentHitSchema);
const personSearchResponseSchema = z.array(personHitSchema);

export type EClaimEntityType = "incident" | "person";

const ENTITY_PATH: Record<EClaimEntityType, "incidents" | "persons"> = {
  incident: "incidents",
  person: "persons",
};

const PAGE_SIZE = 20;

export const SEARCH_DEBOUNCE_MS = 250;
export const SEARCH_MIN_QUERY_CHARS = 2;

async function searchEclaimEntity(entityType: EClaimEntityType, query: string, signal: AbortSignal): Promise<EntityHit[]> {
  const params = new URLSearchParams({ search: `${query}*`, offset: "0", size: String(PAGE_SIZE) });
  const url = `${env.VITE_CLAIMINI_ADAPTER_BASE_URL}/search/v1/${ENTITY_PATH[entityType]}?${params.toString()}`;

  const response = await authenticatedFetch(url, { signal });
  if (!response.ok) throw new Error(`eClaim search failed with status ${response.status}`);

  const schema = entityType === "incident" ? incidentSearchResponseSchema : personSearchResponseSchema;
  return schema.parse(await response.json());
}

// eClaim search terms and claiminiId comparisons share one canonical form (ids are stored
// trimmed). Use this everywhere a value is keyed, sent, guarded, or compared. One deliberate
// exception: EntitySearchInput's committed-value existence check compares untrimmed, because
// it validates the exact payload submission would send.
export const normalizeEclaimValue = (value: string | number) => String(value).trim();

export function eclaimEntitySearchQueryOptions(entityType: EClaimEntityType, debouncedQuery: string, enabled = true) {
  const trimmed = normalizeEclaimValue(debouncedQuery);
  return queryOptions({
    queryKey: ["eclaim-search", entityType, trimmed],
    queryFn: ({ signal }) => searchEclaimEntity(entityType, trimmed, signal),
    enabled: enabled && trimmed.length >= SEARCH_MIN_QUERY_CHARS,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    placeholderData: keepPreviousData,
    // Debounced search is self-healing — the next keystroke produces a new
    // queryKey, so retrying a failed query just delays user feedback.
    retry: false,
  });
}

// Existence lookups: same queryKey as the search (prefetch + popover share the cache), but
// never stale — otherwise every focus/reconnect burst-refetches one query per mounted suggestion.
export function eclaimEntityLookupQueryOptions(entityType: EClaimEntityType, value: string, enabled = true) {
  return queryOptions({
    ...eclaimEntitySearchQueryOptions(entityType, value, enabled),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
